import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { Button } from '../../../../group_leader/components/common'
import { GGCheckbox } from '../../../../shared/new_components/GGCheckbox'
import { GGRadio } from '../../../../shared/new_components/GGRadio'
import { makeT, sharedT } from '../../../../group_leader/helpers/i18n'
import { useDialogFocus } from '../../../../group_leader/hooks/useDialogFocus'
import OptimizationOptions from './OptimizationOptions'

// Re-deals every golfer by a chosen algorithm, with its options.

const t = makeT('group_leader.event_setup.pairings')

const ReorganizeModal = ({ onClose, onSubmit, groupSize, customFields, submitting, placing }) => {
  const [ algorithm, setAlgorithm ] = useState('random')
  const [ optimization, setOptimization ] = useState(groupSize <= 4 ? '3' : '4')
  const [ metadatumId, setMetadatumId ] = useState('')
  const [ consolidate, setConsolidate ] = useState(false)
  const boxRef = useRef(null)
  useDialogFocus(boxRef, onClose)

  const showAB = groupSize === 4 || groupSize === 6
  const abcdLabel = Array.from({ length: groupSize }, (_, i) => String.fromCharCode(65 + i)).join('')
  const showBalance = groupSize <= 4
  const entryNumberField = customFields.find((cf) => cf.name === 'Entry Number')
  const hasCustomFields = customFields.length > 0

  useEffect(() => {
    if (!metadatumId && customFields.length > 0) { setMetadatumId(customFields[0].id) }
  }, [ customFields, metadatumId ])

  // The shared radio; the row's layout and hover are the wizard's.
  const algorithmOption = (value, label) => (
    <GGRadio
      className="gl-event-setup-rg-opt"
      name="rg-algorithm"
      value={value}
      selected={algorithm}
      onChange={setAlgorithm}
      label={label}
    />
  )
  const optimizationOptions = (
    <OptimizationOptions value={optimization} onChange={setOptimization} showBalance={showBalance}/>
  )

  const handleSubmit = () => {
    onSubmit({
      algorithm,
      optimization,
      metadatumId,
      consolidate,
      entryNumberFieldId: entryNumberField?.id,
    })
  }

  return createPortal(
    <div className="gl-event-setup-rg-overlay" onClick={onClose}>
      <div
        ref={boxRef}
        className="gl-event-setup-rg-box"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('reorganize.aria_label')}
      >
        <h3 className="gl-event-setup-rg-title">{t(placing ? 'reorganize.title_place' : 'reorganize.title')}</h3>

        {algorithmOption('random', t('reorganize.random'))}

        {showAB && algorithmOption('ab', 'AB')}
        {algorithm === 'ab' && optimizationOptions}

        {algorithmOption('abcd', abcdLabel)}
        {algorithm === 'abcd' && optimizationOptions}

        {entryNumberField && algorithmOption('entry_number', t('reorganize.by_entry_number'))}

        {hasCustomFields && (
          <>
            <hr className="gl-event-setup-rg-divider"/>
            {algorithmOption('custom_field', t('reorganize.by_custom_field'))}
            {algorithm === 'custom_field' && (
              <>
                <div className="gl-event-setup-rg-select-row">
                  <select
                    className="gl-event-setup-rg-select"
                    value={metadatumId}
                    onChange={(e) => setMetadatumId(e.target.value)}
                    aria-label={t('reorganize.by_custom_field')}
                  >
                    {customFields.map((cf) => (
                      <option key={cf.id} value={cf.id}>{cf.name}</option>
                    ))}
                  </select>
                </div>
                <GGCheckbox
                  className="gl-event-setup-rg-check-row"
                  checked={consolidate}
                  onChange={setConsolidate}
                  label={t('reorganize.consolidate')}
                />
              </>
            )}
          </>
        )}

        <div className="gl-event-setup-rg-actions">
          <Button variant="secondary" size="lg" onClick={onClose} disabled={submitting}>
            {sharedT('cancel')}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={submitting}>
            {t(placing ? 'sheet.place_all' : 'sheet.reorganize')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

ReorganizeModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  groupSize: PropTypes.number.isRequired,
  customFields: PropTypes.array.isRequired,
  submitting: PropTypes.bool,
  // Placing an empty sheet rather than re-dealing a full one: same choices, different words.
  placing: PropTypes.bool,
}

export default ReorganizeModal
